/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import moment, { Moment } from 'moment'
import { Checkbox, EmptyData, Input } from '@intelie/ui'

import LiveSelect from 'live/components/forms/ui/select'
import FormGroup from 'live/components/forms/ui/form-group'
import i18n from 'live/services/i18n'

import { BuilderCheckboxContainer } from '../../template/styles'
import { RigSelector } from '../selectors/RigSelector'
import { WellSelector } from '../selectors/WellSelector'
import { CrewSelector } from '../selectors/CrewSelector'
import { PeriodContainer } from '../styles'
import CustomDatePicker from './CustomDatePicker'
import { Period } from '../../types'

interface RendererProps {
    mode?: 'edit' | 'preview' | 'readOnly'
    data?: Record<string, unknown>
    schema: FormSchema
    period?: Period
    onChange?: (data: Record<string, unknown>) => void
    onChangePeriod?: (date: Moment, uuid: string) => void
}

export const FormRenderer: FC<RendererProps> = ({
    mode,
    data,
    schema,
    period,
    onChange,
    onChangePeriod
}: RendererProps) => {
    const [userData, setUserData] = useState<Record<string, unknown>>(data || {})

    const handleOnChange = (value, key): void => {
        if (mode === 'edit') {
            setUserData(prevState => ({ ...prevState, [key]: value }))
        }
    }

    const renderElement = (element: FormElement): Element | React.ReactElement | JSX.Element => {
        const key = element.name ?? element.uuid
        const value = userData[key]

        switch (element.type) {
            case 'text':
                return (
                    <Input
                        className="form-group"
                        type={element.type}
                        disabled={element.props.readOnly || mode === 'readOnly'}
                        placeholder={element.props.placeholder}
                        value={value as string}
                        label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                        inputState={mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'default'}
                        onChange={e => {
                            handleOnChange(e.target.value, key)
                        }}
                    />
                )
            case 'number':
                return (
                    <Input
                        className="form-group"
                        type={element.type}
                        disabled={element.props.readOnly || mode === 'readOnly'}
                        placeholder={element.props.placeholder}
                        value={value as number}
                        label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                        inputState={mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'default'}
                        onChange={e => {
                            handleOnChange(e.target.value, key)
                        }}
                    />
                )
            case 'date':
                return (
                    <CustomDatePicker
                        className="form-group"
                        title={element.props.label}
                        disabled={element.props.readOnly || mode === 'readOnly'}
                        required={element.props.required}
                        format={element.props.placeholder}
                        date={value ? moment(value) : null}
                        onChange={date => handleOnChange(date, key)}
                        validationState={
                            mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'default'
                        }
                    />
                )
            case 'datetime-local':
                return (
                    <CustomDatePicker
                        time
                        className="form-group"
                        title={element.props.label}
                        disabled={element.props.readOnly || mode === 'readOnly'}
                        required={element.props.required}
                        format={element.props.placeholder}
                        date={value ? moment(value) : null}
                        onChange={e => handleOnChange(e, key)}
                        validationState={
                            mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'default'
                        }
                    />
                )
            case 'textarea':
                return (
                    <FormGroup
                        componentClass="textarea"
                        label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                        name={element.props.label}
                        placeholder={element.props.placeholder}
                        value={value}
                        onChange={e => handleOnChange(e.target.value, key)}
                        disabled={element.props.readOnly || mode === 'readOnly'}
                        className="textarea textarea-md"
                        validationState={
                            mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'success'
                        }
                    />
                )
            case 'select':
                return (
                    <LiveSelect
                        clearable={true}
                        options={(element.props as ListProps).options}
                        label={`${element.props.label} ${element.props.required ? '*' : ''}`}
                        value={value}
                        placeholder={element.props.placeholder}
                        onChange={e => handleOnChange(e, key)}
                        disabled={element.props.readOnly || mode === 'readOnly'}
                        validationState={
                            mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'success'
                        }
                    />
                )
            case 'asset':
                return renderAssetSelector(element)
            case 'checkbox':
                return (
                    <BuilderCheckboxContainer>
                        <Checkbox
                            checked={value as boolean}
                            onChange={e => handleOnChange(e.target.checked, key)}
                            disabled={element.props.readOnly || mode === 'readOnly'}
                        />
                        <label>{`${element.props.label} ${element.props.required ? '*' : ''}`}</label>
                    </BuilderCheckboxContainer>
                )
            case 'group':
                return (
                    <HorizontalGroup>
                        {element.children?.map(child => {
                            return renderElement(child)
                        })}
                    </HorizontalGroup>
                )
            default:
                return <></>
        }
    }

    const renderAssetSelector = (element: FormElement): JSX.Element => {
        const key = element.name ?? element.uuid
        const value = userData[key]

        const selectorBaseProps = {
            onChange: asset => {
                handleOnChange(asset, key)
            },
            mode: mode,
            label: element.props.label,
            placeholder: element.props.placeholder,
            required: element.props.required,
            readOnly: element.props.readOnly,
            validationState: mode === 'edit' && !userData[key] && element.props.required ? 'error' : 'success'
        } as AssetSelectorBaseProps

        switch ((element.props as AssetProps).assetType) {
            case 'rig':
                return <RigSelector {...selectorBaseProps} rig={value} />
            case 'well':
                return <WellSelector {...selectorBaseProps} well={value} />
            case 'crew':
                return <CrewSelector {...selectorBaseProps} crew={value} />
            default:
                return <></>
        }
    }

    useEffect(() => {
        if (onChange) {
            onChange(userData)
        }
    }, [userData])

    useEffect(() => {
        if (!data) {
            setUserData({})
        }
    }, [data])

    return (
        <>
            <PeriodContainer>
                <div className="from-date">
                    <CustomDatePicker
                        time
                        required
                        id="fromDate"
                        name="fromDate"
                        title={i18n('From')}
                        className="date-picker"
                        date={mode === 'preview' ? null : moment(period?.begin)}
                        disabled={mode === 'readOnly'}
                        onChange={date => onChangePeriod(date, 'begin')}
                    />
                </div>
                <div className="to-date">
                    <CustomDatePicker
                        time
                        id="toDate"
                        name="toDate"
                        className="date-picker"
                        date={period?.end ? moment(period?.end) : null}
                        title={i18n('To (optional)')}
                        disabled={mode === 'readOnly'}
                        onChange={date => onChangePeriod(date, 'end')}
                    />
                </div>
            </PeriodContainer>
            {schema?.elements ? (
                schema.elements.map(element => {
                    return renderElement(element as FormElement)
                })
            ) : (
                <EmptyData title={i18n('No form built yet')} icon="far fa-folder-open" />
            )}
        </>
    )
}

const HorizontalGroup = styled.div`
    flex-direction: row;
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 16px 0;

    * {
        cursor: pointer;
    }

    > div {
        width: 100%;
        border-radius: 4px;
        //padding: 8px;
    }

    .form-group {
        margin: 0;
    }
`
